import { z } from 'zod'

// Legacy compared the flags with `=== 'true'`; any other value (or none) is off.
const flag = z
  .string()
  .optional()
  .transform((value) => value === 'true')

const optionalText = z
  .string()
  .optional()
  .transform((value) => value?.trim() || null)

const url = z.string({ required_error: 'is required' }).trim().url('must be an absolute URL')

const envSchema = z.object({
  // Scripts build `${apiUrl}system/locales`, so the trailing slash is normalised here.
  VITE_API_URL: url.transform((value) => (value.endsWith('/') ? value : `${value}/`)),
  VITE_API_KEY: z.string({ required_error: 'is required' }).trim().min(1, 'is required'),
  VITE_BASE_URL: url.transform((value) => value.replace(/\/+$/, '')),
  VITE_DEV_MODE: flag,
  VITE_BETA_FEATURE: flag,
  VITE_TIME_ENTRIES: flag,
  VITE_MAINTENANCE_MODE: flag,
  VITE_BUILD_VERSION: z
    .string()
    .optional()
    .transform((value) => value?.trim() || null),
  VITE_USE_PLACEHOLDER_UNTRANSLATED: flag,
  VITE_CRISP_WEBSITE_ID: optionalText,
  VITE_GA_MEASUREMENT_ID: optionalText,
})

export interface AppConfig {
  /** API base URL, always ending with `/` (e.g. `https://stage-api.skileo.com/v1/`). */
  apiUrl: string
  /** The public application key, sent as `Api-Key` on every request. */
  apiKey: string
  /** Public web URL without a trailing slash. */
  baseUrl: string
  devMode: boolean
  betaFeature: boolean
  /**
   * The API has the time entries that replace the timesheets (docs/projects PLAN.md D5, API.md §6): the HR timesheet
   * screens use them instead of `timesheets/*`. Off until the backend ships `time/*` (stage runs with the beta flag
   * on, so beta cannot gate this); on in the mock and the e2e runs.
   */
  timeEntries: boolean
  maintenanceMode: boolean
  buildVersion: string | null
  usePlaceholderUntranslated: boolean
  /** Crisp chat website id (D-08); the chat is not loaded without it. */
  crispWebsiteId: string | null
  /** Google Analytics measurement id (D-08); no tracking without it. */
  gaMeasurementId: string | null
}

export class ConfigError extends Error {
  readonly issues: string[]

  constructor(issues: string[]) {
    super(`Invalid environment configuration: ${issues.join('; ')}`)
    this.name = 'ConfigError'
    this.issues = issues
  }
}

export function parseConfig(env: Record<string, unknown>): AppConfig {
  const result = envSchema.safeParse(env)
  if (!result.success) {
    throw new ConfigError(result.error.issues.map((issue) => `${issue.path.join('.')} ${issue.message}`))
  }
  const value = result.data
  return {
    apiUrl: value.VITE_API_URL,
    apiKey: value.VITE_API_KEY,
    baseUrl: value.VITE_BASE_URL,
    devMode: value.VITE_DEV_MODE,
    betaFeature: value.VITE_BETA_FEATURE,
    timeEntries: value.VITE_TIME_ENTRIES,
    maintenanceMode: value.VITE_MAINTENANCE_MODE,
    buildVersion: value.VITE_BUILD_VERSION,
    usePlaceholderUntranslated: value.VITE_USE_PLACEHOLDER_UNTRANSLATED,
    crispWebsiteId: value.VITE_CRISP_WEBSITE_ID,
    gaMeasurementId: value.VITE_GA_MEASUREMENT_ID,
  }
}

let current: AppConfig | null = null

/** Validates the build-time environment once, at boot (src/app/main.ts). Throws a ConfigError naming each bad key. */
export function loadConfig(env: Record<string, unknown> = import.meta.env): AppConfig {
  current = parseConfig(env)
  return current
}

/** The validated configuration; throws when called before loadConfig(). */
export function getConfig(): AppConfig {
  if (!current) throw new ConfigError(['loadConfig() has not run'])
  return current
}

/** Test seam: installs a configuration without reading the environment. */
export function setConfig(config: AppConfig | null): void {
  current = config
}
